import { Typography } from '@design-system/components/typography/typography';
import {
  BundleCardItem,
  BundleCardItemFeatures,
  StandardBundleCardData,
} from '../viewModel/otpBundles.viewModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { isEmpty } from 'lodash';
import { Tag } from '@design-system/components/tag/tag';
import { TooltipWrapper } from '../../view/TooltipWrapper';
import { Chip } from '@design-system/components/chip/chip';
import { Skeleton } from '@mui/material';

export const OtpBundleCard = ({ menu, data, onClick }: BundleCardItem) => {
  if (data.type === 'submitting') {
    return (
      <div className="flex flex-col gap-4 p-4 bg-white rounded-[10px] shadow-exercise w-[515px] cursor-pointer">
        <div className="flex justify-between">
          <Typography variant="headline" size="medium">
            {data.title}
          </Typography>
        </div>
        <div className="flex flex-col gap-4">
          <Skeleton variant="rectangular" width="100%" height={30} />
          <Skeleton variant="rectangular" width="100%" height={60} />
          <Skeleton variant="rectangular" width="100%" height={60} />
          <Skeleton variant="rectangular" width="100%" height={30} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-4 p-4 bg-white rounded-[10px] shadow-exercise w-[515px] cursor-pointer"
      onClick={onClick}
    >
      <div className="flex justify-between">
        <Typography variant="headline" size="medium">
          {data.title}
        </Typography>
        <BundleCardMenu menu={menu} />
      </div>
      <div className="flex gap-4 items-center">
        <Typography variant="body" size="medium" className="text-[18px]">
          {data.price}
        </Typography>
        <Typography variant="body" size="medium">
          {data.details}
        </Typography>
      </div>
      <BundleProgramsAndContent
        programs={data.programs}
        content={data.content}
      />
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <FeaturesStatusLabel features={data.features} />
        <Tag
          label={data.status.label}
          className={clsx(
            'capitalize',
            sprinkles({
              bg: data.status.color.bg,
              color: data.status.color.text,
            }),
          )}
          readonly={true}
        />
      </div>
    </div>
  );
};

const BundleProgramsAndContent = ({
  programs,
  content,
}: {
  programs: StandardBundleCardData['programs'];
  content: StandardBundleCardData['content'];
}) => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col gap-2">
      <Typography size="small" variant="body" color="N-60">
        PROGRAMS
      </Typography>
      {isEmpty(programs) ? (
        '-'
      ) : (
        <div className="flex flex-wrap gap-4">
          {programs.map((i, index) => (
            <TooltipWrapper key={index} tooltip={i.tooltipItems}>
              <Chip label={i.title} readonly={true} />
            </TooltipWrapper>
          ))}
        </div>
      )}
    </div>
    <div className="flex flex-col gap-1">
      <Typography size="small" variant="body" color="N-60">
        CONTENT ITEMS
      </Typography>
      {isEmpty(content) ? (
        '-'
      ) : (
        <div className="flex flex-wrap gap-4">
          {content.map((i, index) => (
            <TooltipWrapper key={index} tooltip={i.tooltipItems}>
              <Chip label={i.title} readonly={true} />
            </TooltipWrapper>
          ))}
        </div>
      )}
    </div>
  </div>
);

const FeaturesStatusLabel = ({
  features,
}: {
  features: BundleCardItemFeatures;
}) => (
  <div className="flex gap-4 items-center">
    {features.map(({ name, checked, id }) => (
      <div className="flex gap-2 items-center" key={id}>
        <FontAwesomeIcon
          icon={checked ? faCheck : faXmark}
          className={sprinkles({
            color: checked ? 'B-100' : 'N-60',
          })}
        />
        <Typography
          variant="body"
          size="medium"
          fontWeight="normal"
          className={sprinkles({
            color: checked ? 'B-100' : 'N-60',
          })}
        >
          {name}
        </Typography>
      </div>
    ))}
  </div>
);

const BundleCardMenu = ({ menu }: { menu: BundleCardItem['menu'] }) => (
  <div className="flex gap-2">
    {menu.map((item, index) => (
      <TooltipWrapper tooltip={item.tooltip} key={index}>
        <div
          onClick={(ev) => {
            ev.stopPropagation();
            item?.onClick && item?.onClick();
          }}
          className={clsx(
            'h-[40px] w-[30px] p-2 rounded-md flex items-center justify-center',
            !item.disabled && sprinkles({ bg: { hover: 'N-20' } }),
            item?.onClick && 'cursor-pointer',
            !item?.onClick && 'cursor-default',
            item.disabled && 'pointer-events-none',
          )}
        >
          <FontAwesomeIcon
            icon={item.icon}
            className={sprinkles({
              color: item?.disabled ? 'N-40' : 'B-100',
            })}
          />
        </div>
      </TooltipWrapper>
    ))}
  </div>
);
